// #region IMPORTS
import { useCallback, useEffect, useState } from 'react';

import {
  findReportSeriesOfGroup,
  findShownCandidate,
  fromDisplaySets,
  groupKeyToResolveIn,
} from '../../AZmed/Data/reportSeries';
import { fetchReport } from './reportAPI';
// #endregion

// #region CONSTANTS
// Outcome placeholder whose key matches nothing, so it is never current.
const NOTHING_LOADED = { uid: Symbol('nothing'), report: null, error: null };
// #endregion

// #region HOOK
/**
 * Loads from Orthanc the report of the group shown in the active viewport.
 *
 * @param {Object} study The active study
 * @param {string} [activeDisplaySetInstanceUID] Display set of the active viewport
 * @returns {Object} `report`, `isLoading`, `error` and `retry`
 */
export const useStudyReport = (study, activeDisplaySetInstanceUID) => {
  // #region INITIALIZATION
  // Outcome keyed by study/series so a previous patient's report is never shown.
  const [loaded, setLoaded] = useState(NOTHING_LOADED);

  // Bumped by retry to rerun the effect on the same key.
  const [retryToken, setRetryToken] = useState(0);

  const studyInstanceUID = study && study.StudyInstanceUID;

  // Read on every render: study.displaySets is reassigned as series arrive.
  const displaySets = (study && study.displaySets) || null;

  const candidates = fromDisplaySets(displaySets);

  // Viewport (not region filter) decides the shown group.
  const shown = findShownCandidate(candidates, activeDisplaySetInstanceUID);

  // Group of the shown series, or the single group for an acquisition series.
  const groupKey = groupKeyToResolveIn(candidates, shown);

  const reportSeries = findReportSeriesOfGroup(candidates, groupKey);
  const seriesInstanceUID = reportSeries && reportSeries.SeriesInstanceUID;

  // Distinguishes "not known yet" (metadata or viewport pending) from "no report".
  const isResolving =
    !studyInstanceUID || !displaySets || (displaySets.length > 0 && !shown);

  // Outcome key: the report series, or the study when no series carries a report.
  const activeKey = seriesInstanceUID || studyInstanceUID;
  // #endregion

  // #region EVENT
  // Starts a new attempt after a failure.
  const retry = useCallback(() => {
    // Clears the outcome so the button reacts immediately.
    setLoaded(NOTHING_LOADED);
    setRetryToken(token => token + 1);
  }, []);
  // #endregion

  // #region HOOK
  useEffect(() => {
    if (!activeKey || isResolving) {
      return undefined;
    }

    // No report series in the shown group: conclude "no report" under the key.
    if (!seriesInstanceUID) {
      setLoaded({ uid: activeKey, report: null, error: null });

      return undefined;
    }

    // Keeps a slow answer for a previous key from overwriting the current one.
    let cancelled = false;

    fetchReport(seriesInstanceUID)
      .then(report => {
        if (!cancelled) {
          setLoaded({ uid: activeKey, report, error: null });
        }
      })
      .catch(error => {
        if (cancelled) {
          return;
        }

        console.error('[useStudyReport] Error loading the report:', error);
        setLoaded({ uid: activeKey, report: null, error });
      });

    return () => {
      cancelled = true;
    };
  }, [activeKey, seriesInstanceUID, isResolving, retryToken]);
  // #endregion

  // #region INTERFACE
  // Only the outcome stamped with the active key may be shown.
  const isCurrent = loaded.uid === activeKey;
  const report = isCurrent ? loaded.report : null;
  const error = isCurrent ? loaded.error : null;

  return {
    report,
    error,
    // Not concluded yet for this key: metadata, viewport or fetch pending.
    isLoading: !report && !error && (isResolving || !isCurrent),
    retry,
  };
  // #endregion
};
// #endregion
